import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, AlertTriangle, CalendarDays, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import WeeklyMenuDay from '@/components/WeeklyMenuDay';
import AddMealDialog from '@/components/AddMealDialog';
import { useWeeklyMenu } from '@/hooks/useWeeklyMenu';
import { useAuth } from '@/contexts/AuthContext';
import { Recipe, DayMeals, DayOfWeek, MealType } from '@/types';
import { useToast } from '@/hooks/use-toast';

// Interface para el menú simplificado
interface SimplifiedMealPlan {
  [day: string]: DayMeals;
}

const WeeklyMenu = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoggedIn } = useAuth();
  const { currentMenu, loading, error, addRecipeToMenu, removeRecipeFromMenu, refetch } = useWeeklyMenu();

  const [recommendedCalories, setRecommendedCalories] = useState<number>(0);
  const [mealPlan, setMealPlan] = useState<SimplifiedMealPlan>({});
  const [addMealDialog, setAddMealDialog] = useState({
    isOpen: false,
    day: '',
    mealType: ''
  });

  // Días de la semana en español
  const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Mapeo de español a DayOfWeek (sin acentos para coincidir con el backend)
  const spanishToDay: { [key: string]: DayOfWeek; } = {
    'Lunes': DayOfWeek.LUNES,
    'Martes': DayOfWeek.MARTES,
    'Miércoles': DayOfWeek.MIERCOLES,
    'Jueves': DayOfWeek.JUEVES,
    'Viernes': DayOfWeek.VIERNES,
    'Sábado': DayOfWeek.SABADO,
    'Domingo': DayOfWeek.DOMINGO
  };

  // Mapeo de español a MealType
  const spanishToMealType: { [key: string]: MealType; } = {
    'desayuno': MealType.DESAYUNO,
    'comida': MealType.COMIDA,
    'cena': MealType.CENA,
    'snack': MealType.SNACK
  };

  // Convertir datos del backend al formato del frontend - SIMPLIFICADO
  useEffect(() => {
    if (!currentMenu?.dayMeals) {
      // Inicializar estructura vacía
      const emptyMealPlan: SimplifiedMealPlan = {};
      daysOfWeek.forEach(day => {
        emptyMealPlan[day] = {
          desayuno: [],
          comida: [],
          cena: [],
          snack: []
        };
      });
      setMealPlan(emptyMealPlan);
      return;
    }

    const convertedMealPlan: SimplifiedMealPlan = {};

    // Inicializar todos los días
    daysOfWeek.forEach(day => {
      convertedMealPlan[day] = {
        desayuno: [],
        comida: [],
        cena: [],
        snack: []
      };
    });

    // Llenar con datos del backend
    Object.entries(currentMenu.dayMeals).forEach(([dayKey, dayData]) => {
      // Buscar el día en español que corresponda
      const spanishDay = Object.entries(spanishToDay).find(
        ([spanish, english]) => english === dayKey
      )?.[0];

      if (spanishDay && dayData) {
        Object.entries(dayData).forEach(([mealTypeKey, recipes]) => {
          // Buscar el tipo de comida en español que corresponda
          const spanishMealType = Object.entries(spanishToMealType).find(
            ([spanish, english]) => english === mealTypeKey
          )?.[0];

          if (spanishMealType && Array.isArray(recipes)) {
            convertedMealPlan[spanishDay][spanishMealType as keyof DayMeals] = recipes;
          }
        });
      }
    });

    setMealPlan(convertedMealPlan);
  }, [currentMenu]);

  // Load user profile on component mount
  useEffect(() => {
    const userProfile = localStorage.getItem('userProfile');
    if (userProfile) {
      try {
        const profile = JSON.parse(userProfile);
        setRecommendedCalories(profile.recommendedCalories || 0);
      } catch (error) {
        console.error('Error parsing user profile:', error);
      }
    }
  }, []);

  const addMeal = async (recipe: Recipe) => {
    const { day, mealType } = addMealDialog;

    if (!currentMenu) {
      toast({
        title: "Error",
        description: "No se pudo cargar el menú semanal",
        variant: "destructive",
      });
      return;
    }

    const dayOfWeek = spanishToDay[day];
    const mealTypeEnum = spanishToMealType[mealType];

    if (!dayOfWeek || !mealTypeEnum) {
      toast({
        title: "Error",
        description: "Día o tipo de comida inválido",
        variant: "destructive",
      });
      return;
    }

    const success = await addRecipeToMenu(dayOfWeek, mealTypeEnum, recipe.id);

    if (success) {
      toast({
        title: "¡Éxito!",
        description: `${recipe.title} se ha agregado al menú del ${day} para ${mealType}`,
      });
      setAddMealDialog({ isOpen: false, day: '', mealType: '' });
      // Recargar el menú para reflejar los cambios
      refetch();
    } else {
      toast({
        title: "Error",
        description: "No se pudo agregar la receta al menú",
        variant: "destructive",
      });
    }
  };

  const removeMeal = async (day: string, mealType: string, recipeId: number) => {
    console.log(`🗑️ Attempting to remove recipe ${recipeId} from ${day}.${mealType}`);

    if (!currentMenu) {
      toast({
        title: "Error",
        description: "No se pudo cargar el menú semanal",
        variant: "destructive",
      });
      return;
    }

    const dayOfWeek = spanishToDay[day];
    const mealTypeEnum = spanishToMealType[mealType];

    if (!dayOfWeek || !mealTypeEnum) {
      toast({
        title: "Error",
        description: "Día o tipo de comida inválido",
        variant: "destructive",
      });
      return;
    }

    const success = await removeRecipeFromMenu(dayOfWeek, mealTypeEnum);

    if (success) {
      toast({
        title: "Receta eliminada",
        description: "La receta se ha eliminado del menú semanal",
      });
      // No necesitas refetch() aquí porque el hook ya actualiza el estado
    } else {
      toast({
        title: "Error",
        description: "No se pudo eliminar la receta del menú",
        variant: "destructive",
      });
    }
  };

  const calculateDayTotals = (dayMeals: DayMeals) => {
    const allMeals = [...dayMeals.desayuno, ...dayMeals.comida, ...dayMeals.cena, ...dayMeals.snack];
    return allMeals.reduce(
      (totals, recipe) => ({
        calories: totals.calories + (recipe.calories || 0),
        protein: totals.protein + (recipe.protein || 0),
        carbs: totals.carbs + (recipe.carbs || 0),
        fat: totals.fat + (recipe.fat || 0)
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const getMacroAlerts = (totals: { calories: number; protein: number; carbs: number; fat: number; }) => {
    const alerts: Array<{ type: string; message: string; }> = [];
    const totalMacros = totals.protein + totals.carbs + totals.fat;

    if (totalMacros > 0) {
      const proteinPercentage = (totals.protein / totalMacros) * 100;
      const carbsPercentage = (totals.carbs / totalMacros) * 100;
      const fatPercentage = (totals.fat / totalMacros) * 100;

      if (proteinPercentage > 60) {
        alerts.push({ type: 'protein', message: `Alta proteína: ${Math.round(proteinPercentage)}%` });
      }
      if (carbsPercentage > 60) {
        alerts.push({ type: 'carbs', message: `Altos carbohidratos: ${Math.round(carbsPercentage)}%` });
      }
      if (fatPercentage > 60) {
        alerts.push({ type: 'fat', message: `Alta grasa: ${Math.round(fatPercentage)}%` });
      }
    }

    if (recommendedCalories > 0 && totals.calories > recommendedCalories) {
      alerts.push({
        type: 'calories',
        message: `Excede la meta por ${totals.calories - recommendedCalories} cal`
      });
    }

    return alerts;
  };

  const generateShoppingList = () => {
    const allIngredients: string[] = [];

    Object.values(mealPlan).forEach((dayMeals: DayMeals) => {
      const allMeals = [...dayMeals.desayuno, ...dayMeals.comida, ...dayMeals.cena, ...dayMeals.snack];
      allMeals.forEach((recipe: Recipe) => {
        if (recipe.ingredients && recipe.ingredients.length > 0) {
          allIngredients.push(...recipe.ingredients);
        } else {
          allIngredients.push(`Ingredientes para: ${recipe.title}`);
        }
      });
    });

    return [...new Set(allIngredients)];
  };

  const isCalorieExceeded = (dayCalories: number) => {
    return recommendedCalories > 0 && dayCalories > recommendedCalories;
  };

  const isMenuEmpty = Object.values(mealPlan).every((dayMeals: DayMeals) =>
    Object.values(dayMeals).every((meals: Recipe[]) => meals.length === 0)
  );

  // Resto del componente se mantiene igual desde aquí...
  // [El resto del código del componente permanece igual]

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarDays className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2 text-center">
              Inicia sesión para ver tu menú semanal
            </h3>
            <p className="text-gray-500 text-center mb-6">
              Accede a tu cuenta para planificar tus comidas de la semana
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <Card>
          <CardContent className="flex justify-center items-center py-12">
            <div className="text-lg text-gray-600">Cargando menú semanal...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <Card>
          <CardContent className="text-center py-12">
            <div className="text-red-600 mb-4">Error: {error}</div>
            <Button onClick={refetch} variant="outline">
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-primary/10 rounded-full transition-all duration-200"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Menú Semanal</h1>
                <p className="text-sm text-muted-foreground">Planifica tus comidas para la semana</p>
                {currentMenu?.weekStartDate && (
                  <Badge variant="secondary" className="mt-1">
                    Semana del {new Date(currentMenu.weekStartDate).toLocaleDateString('es-ES')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Calorie Goal Alert */}
        {recommendedCalories > 0 && (
          <Alert className="bg-gradient-to-r from-primary/10 to-blue-50 border-primary/20 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
              <AlertDescription className="text-primary font-medium">
                <strong>Meta Calórica Diaria:</strong> {recommendedCalories} calorías
                {' - '}
                <button
                  onClick={() => navigate('/profile')}
                  className="story-link font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  Ajustar en perfil
                </button>
              </AlertDescription>
            </div>
          </Alert>
        )}

        {/* Days Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {daysOfWeek.map((day, index) => {
            const dayMeals = mealPlan[day] || {
              desayuno: [], comida: [], cena: [], snack: []
            };
            const totals = calculateDayTotals(dayMeals);
            const totalMacros = totals.protein + totals.carbs + totals.fat;
            const isExceeded = isCalorieExceeded(totals.calories);
            const alerts = getMacroAlerts(totals);
            const isEmpty = Object.values(dayMeals).every((meals: Recipe[]) => meals.length === 0);

            return (
              <div
                key={day}
                className="animate-fade-in hover-scale"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card className={`h-fit backdrop-blur-sm border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 group ${isEmpty
                  ? 'bg-gradient-to-br from-gray-50 to-white border-dashed border-2 border-gray-300 hover:border-primary/40'
                  : 'bg-white/70'
                  }`}>
                  <CardHeader className={`pb-4 rounded-t-lg ${isEmpty
                    ? 'bg-gradient-to-b from-gray-100/50 to-transparent'
                    : 'bg-gradient-to-b from-primary/5 to-transparent'
                    }`}>
                    <CardTitle className={`text-lg text-center font-bold transition-colors ${isEmpty
                      ? 'text-gray-500 group-hover:text-primary'
                      : 'text-gray-900 group-hover:text-primary'
                      }`}>
                      {day}
                    </CardTitle>

                    {isEmpty ? (
                      <div className="text-center py-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full mb-3 group-hover:bg-primary/20 transition-all duration-200">
                          <CalendarDays className="h-6 w-6 text-gray-400 group-hover:text-primary transition-colors" />
                        </div>
                        <div className="text-sm text-gray-500 mb-3">No hay comidas planeadas</div>
                        <Button
                          size="sm"
                          onClick={() => setAddMealDialog({ isOpen: true, day, mealType: 'desayuno' })}
                          className="bg-primary/10 hover:bg-primary text-primary hover:text-white border-none shadow-none transition-all duration-200"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Agregar Comida
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="text-center">
                          <div className={`text-2xl font-bold transition-colors ${isExceeded ? 'text-red-500' : 'text-primary'
                            }`}>
                            {totals.calories}
                            <span className="text-sm font-normal text-muted-foreground ml-1">cal</span>
                          </div>

                          {isExceeded && (
                            <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200 animate-scale-in">
                              <span className="text-xs text-red-600 font-medium flex items-center justify-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                +{totals.calories - recommendedCalories} cal sobre la meta
                              </span>
                            </div>
                          )}
                        </div>

                        {totalMacros > 0 && (
                          <div className="space-y-2 mt-3">
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className="text-center">
                                <div className="font-semibold text-emerald-600">
                                  {Math.round((totals.protein / totalMacros) * 100)}%
                                </div>
                                <div className="text-emerald-600">Proteína</div>
                                <div className="w-full bg-emerald-100 rounded-full h-1.5 mt-1">
                                  <div
                                    className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${(totals.protein / totalMacros) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="font-semibold text-amber-600">
                                  {Math.round((totals.carbs / totalMacros) * 100)}%
                                </div>
                                <div className="text-amber-600">Carbos</div>
                                <div className="w-full bg-amber-100 rounded-full h-1.5 mt-1">
                                  <div
                                    className="bg-amber-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${(totals.carbs / totalMacros) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="font-semibold text-purple-600">
                                  {Math.round((totals.fat / totalMacros) * 100)}%
                                </div>
                                <div className="text-purple-600">Grasa</div>
                                <div className="w-full bg-purple-100 rounded-full h-1.5 mt-1">
                                  <div
                                    className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${(totals.fat / totalMacros) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {alerts.length > 0 && (
                          <div className="space-y-2 mt-4">
                            {alerts.map((alert, alertIndex) => (
                              <Alert
                                key={alertIndex}
                                className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 py-2 animate-scale-in"
                                style={{ animationDelay: `${alertIndex * 0.1}s` }}
                              >
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                <AlertDescription className="text-amber-800 text-xs ml-2 font-medium">
                                  {alert.message}
                                </AlertDescription>
                              </Alert>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </CardHeader>

                  {!isEmpty && (
                    <CardContent className="pt-2">
                      <WeeklyMenuDay
                        day={day}
                        dayMeals={dayMeals}
                        onAddMeal={(mealType) => setAddMealDialog({ isOpen: true, day, mealType })}
                        onRemoveMeal={(mealType, recipeId) => removeMeal(day, mealType, recipeId)}
                      />
                    </CardContent>
                  )}
                </Card>
              </div>
            );
          })}
        </div>

        {isMenuEmpty && (
          <div className="text-center py-12 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <CalendarDays className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Comienza a Planificar tu Semana</h3>
            <p className="text-muted-foreground mb-4">Haz clic en los botones + para agregar comidas a tu menú semanal</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary hover:bg-primary/90"
            >
              Explorar Recetas
            </Button>
          </div>
        )}
      </div>

      <AddMealDialog
        isOpen={addMealDialog.isOpen}
        onClose={() => setAddMealDialog({ isOpen: false, day: '', mealType: '' })}
        onAddMeal={addMeal}
        day={addMealDialog.day}
        mealType={addMealDialog.mealType}
      />
    </div>
  );
};

export default WeeklyMenu;